import React, { useContext } from "react"
import { PrimaryButton } from "source/giving/donate/form/components/display/PrimaryButton"
import { LoginForm } from "source/login/LoginForm"
import {
  Modal,
  useCloseModal,
  useOpenModal,
} from "source/shared/components/Modal"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { pushBrowserHistoryState } from "./BrowserNavigationHandler"

interface ContinueButtonProps {
  authenticated: boolean
  validateProgress: () => boolean
}

export function ContinueButton({
  authenticated,
  validateProgress,
}: ContinueButtonProps) {
  const text = "Continue to give"

  return authenticated ? (
    <ContinueToPaymentButton {...{ text, validateProgress }} />
  ) : (
    <Modal>
      <ContinueToLoginButton {...{ text, validateProgress }} />
    </Modal>
  )
}

interface InnerButtonProps {
  text: React.ReactNode
  validateProgress: () => boolean
}

function ContinueToLoginButton({ text, validateProgress }: InnerButtonProps) {
  const { login } = useContext(ServerRenderedProps)
  const { dispatch } = useDonationFormContext()
  const openModal = useOpenModal()
  const closeModal = useCloseModal()

  const handleClick = () => {
    const isValid = validateProgress()
    if (isValid) {
      openModal()
    }
  }

  const handleLoginSuccess = () => {
    closeModal()
    dispatch({ type: "SET_FOCUSED_PANE", payload: "right" })
    pushBrowserHistoryState("right")
  }

  return (
    <>
      <PrimaryButton onClick={handleClick}>{text}</PrimaryButton>
      <Modal.Show>
        <Modal.Header></Modal.Header>
        <Modal.Body>
          <LoginForm
            emailOnly={login.email_only}
            onSuccess={handleLoginSuccess}
            textOverrides={{
              devicePrompt: {
                phone: "Enter your phone number",
                email: "Enter your email address",
              },
              codeDescription:
                "We\u2019ll send you a code to complete your gift securely.",
              secondDevicePrompt: {
                email: "To send your receipt,",
              },
              newPersonPrompt:
                "Enter your first and last name to select your payment method.",
              confirmPerson:
                "Continue to select your payment method and complete your gift.",
            }}
          />
        </Modal.Body>
      </Modal.Show>
    </>
  )
}

function ContinueToPaymentButton({ text, validateProgress }: InnerButtonProps) {
  const { dispatch } = useDonationFormContext()

  const handleClick = () => {
    const isValid = validateProgress()
    if (isValid) {
      dispatch({ type: "SET_FOCUSED_PANE", payload: "right" })
      pushBrowserHistoryState("right")
    }
  }

  return <PrimaryButton onClick={handleClick}>{text}</PrimaryButton>
}
